import { default as React, useEffect, useState } from "react";
import { useLocalization } from "@progress/kendo-react-intl";
import { useServices } from "src/services";
import { useStores } from "src/stores";
import fordScript from "../assets/images/Ford_Script_Blue.png";
import fordOval from "../assets/images/ford-oval.svg";
import videoPoster from "../assets/images/training_video_poster.jpg";
import heroStill from "../assets/images/home_hero_still.jpg";
import PrivacyBanner from "react-ford-ccpa-banner";
import '../styles/_home.scss';

// The program training video, served straight from the CDN as an mp4 so the
// page can use native browser controls rather than the Wistia player chrome.
const TRAINING_VIDEO_URL = 'https://cdn.metinc.tech/downloads/d4urc-2026/2026_Ford_DriveForYourCommunity-TrainingVideo-FINAL-COMPRESSED.mp4';

// The public marketing site (Home / How It Works / Event Resources / Dealer
// Login) lives on its own domain. This portal renders the same Home design and
// links across for the other tabs rather than duplicating those pages.
const getPublicDomain = () => {
  const isLocalhost = window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';
  const isStaging = process.env.NODE_ENV === 'staging' ||
    window.location.hostname.includes('staging');

  if (isLocalhost) return 'http://localhost:8080';
  if (isStaging) return 'https://staging.driveforyourcommunity.com';
  return 'https://driveforyourcommunity.com';
};

// The home page video is awaiting approval, so a still runs in its place.
// Flip this to true to restore the player -- the video markup is kept intact.
const SHOW_HERO_VIDEO = false;

const HomeScreen = () => {
  const localizationService = useLocalization();
  const { language } = localizationService;
  const { d4ur } = useServices();
  const { ui } = useStores();

  // Below 860px the nav tabs and the Dealer Login CTA collapse into a drawer
  // opened by the hamburger; see `.dfyc-nav-toggle` in styles/_home.scss.
  const [ menuOpen, setMenuOpen ] = useState(false);

  // Escape closes the drawer, matching the dismiss affordance keyboard users
  // expect from an expanded menu.
  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [ menuOpen ]);

  // Keeps the shared waitlist flag warm for the rest of the app; the landing
  // page itself no longer branches on it.
  useEffect(() => {
    d4ur.checkWaitlist().then((res) => {
      if (res.result && res.result.events_full === 1) {
        ui.setEventsFull(true);
      }
    });
  }, [d4ur, ui]);

  const publicDomain = getPublicDomain();

  const navTabs = [
    { label: 'Home', href: `${publicDomain}/`, active: true },
    { label: 'How It Works', href: `${publicDomain}/how-it-works` },
    { label: 'Event Resources', href: `${publicDomain}/event-resources` },
  ];

  return (
    <>
      <div id="Home" className="dfyc-landing">

        {/* NAV */}
        <nav className="dfyc-nav">
          <div className="dfyc-nav-inner">
            <div className="dfyc-nav-brand">
              <img src={fordOval} alt="Ford" className="dfyc-ford-mark" />
              <span className="dfyc-brand-divider" />
              <span className="dfyc-program-name">Drive For Your Community</span>
            </div>

            <div className="dfyc-nav-right">
              <ul className="dfyc-nav-links">
                {navTabs.map((tab) => (
                  <li
                    key={tab.label}
                    className={tab.active ? 'dfyc-nav-link-active' : undefined}
                  >
                    <a href={tab.href}>{tab.label}</a>
                  </li>
                ))}
              </ul>
              {/* Dealer Login lives on the public marketing site alongside the
                  other tabs, so this cross-links there rather than to /login. */}
              <a
                href={`${publicDomain}/dealer-login`}
                className="dfyc-btn dfyc-btn-primary dfyc-nav-cta"
              >
                Dealer Login
              </a>

              <button
                type="button"
                className={`dfyc-nav-toggle${menuOpen ? ' dfyc-nav-toggle-open' : ''}`}
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={menuOpen}
                aria-controls="dfyc-nav-drawer"
                onClick={() => setMenuOpen((open) => !open)}
              >
                <span className="dfyc-nav-toggle-bar" />
                <span className="dfyc-nav-toggle-bar" />
                <span className="dfyc-nav-toggle-bar" />
              </button>
            </div>
          </div>

          {/* Kept mounted so `aria-controls` always resolves; `hidden` (and the
              matching CSS guard) does the hiding. These tabs leave for the
              public site, so there is no in-app navigation to close on. */}
          <div id="dfyc-nav-drawer" className="dfyc-nav-drawer" hidden={!menuOpen}>
            <ul className="dfyc-nav-drawer-links">
              {navTabs.map((tab) => (
                <li
                  key={tab.label}
                  className={tab.active ? 'dfyc-nav-drawer-link-active' : undefined}
                >
                  <a href={tab.href}>{tab.label}</a>
                </li>
              ))}
            </ul>

            <a
              href={`${publicDomain}/dealer-login`}
              className="dfyc-btn dfyc-btn-primary dfyc-nav-drawer-cta"
            >
              Dealer Login
            </a>
          </div>
        </nav>

        {/* HERO */}
        <header className="dfyc-hero">
          <div className="dfyc-hero-inner">
            {/* Script is artwork; "Building Together" is live text so it stays
                crisp at any size and the spacing can be tuned in CSS. */}
            <div className="dfyc-hero-lockup">
              <img src={fordScript} alt="Ford" className="dfyc-hero-lockup-script" />
              <span className="dfyc-hero-lockup-wordmark">Building Together</span>
            </div>

            <h1 className="dfyc-page-title">
              Take the wheel.<br />Help your community.
            </h1>

            {/* <p className="dfyc-hero-lead">
              Watch the video below to learn more about the Drive For Your Community Program!
            </p> */}

            {/* PLACEHOLDER: the training video is standing in until the program
                video for the home page is supplied. Swap the src below. */}
            <div className="dfyc-video-frame">
              {SHOW_HERO_VIDEO ? (
                /* preload="metadata" so the 13MB file is not pulled on page load. */
                <video
                  className="dfyc-video-embed"
                  src={TRAINING_VIDEO_URL}
                  poster={videoPoster}
                  controls
                  playsInline
                  preload="metadata"
                />
              ) : (
                <img
                  className="dfyc-video-embed dfyc-video-still"
                  src={heroStill}
                  alt="A volunteer opening the door of a Ford Mustang Mach-E at a Drive For Your Community event"
                />
              )}
            </div>
          </div>
        </header>

        {/* ABOUT THE PROGRAM */}
        <section className="dfyc-label-section">
          <h2 className="dfyc-section-label">About the Program</h2>
          <div className="dfyc-section-body">
            <p>
              Drive For Your Community began in 2007 as Drive 4 UR School, bringing local Ford Dealers and schools together for one-day test-drive events, making a meaningful difference close to home. In 2012, the program grew into Drive 4 UR Community, inspiring thousands of events nationwide and helping raise more than $50 million for local causes.
            </p>
            <p>
              In 2026, the program became Drive For Your Community, expanding to include select school programs and community organizations aligned with Ford Building Together focus areas. Today, every valid test-drive helps drive positive change, with Ford donating $30 to the benefiting organization, up to $6,000 per event.
            </p>
          </div>
        </section>

        {/* FOR DEALERS ONLY / FOR COMMUNITIES */}
        <section className="dfyc-split">
          <div className="dfyc-split-col">
            <div className="dfyc-col-kicker">For Dealers Only</div>
            <h2 className="dfyc-col-heading">Plan Your Event</h2>
            <div className="dfyc-col-copy">
              <p>
                All Ford dealerships are invited to participate on a first-come, first-served basis. Dealer portal login credentials created in 2025 have been carried over and can be used to access the portal to request an event. Ford dealers may host their one-day test-drive event anytime between April 1 and November 30 to support a local nonprofit or community organization.
              </p>
              <p>How to participate:</p>
              <ol>
                <li>Log in to the Dealer Portal.</li>
                <li>
                  Request New Event - Provide essential details, including the dealership, partnering organization, event date, location, and lead contacts.
                </li>
                <li>Await approval of event request.</li>
              </ol>
            </div>
            <div className="dfyc-col-cta">
              <a
                href={`${publicDomain}/dealer-login`}
                className="dfyc-btn dfyc-btn-primary"
              >
                Dealer Login
              </a>
            </div>
          </div>

          <div className="dfyc-split-col">
            <div className="dfyc-col-kicker">For Communities</div>
            <h2 className="dfyc-col-heading">Learn How to Get Involved</h2>
            <div className="dfyc-col-copy">
              <p>
              Drive For Your Community brings together local Ford dealers and nonprofit organizations to support communities and the neighbors they serve.</p>
              <p>
                Ford Dealers team up with local organizations to host a one-day test-drive event. For every test-drive taken at the event, Ford will donate $30 to the organization, up to $6,000.
              </p>
              <p>
                Dealers partner with organizations focused on Building (community development, vocational and trades education, playgrounds and parks) and Rebuilding (disaster response, recovery, and local first responders).
              </p>
              <p>
                Start by reaching out to your local Ford dealer to learn more about eligibility and how to participate.
              </p>
              <p>
                For additional support, contact Program HQ at{' '}
                <a href="mailto:hello@driveforyourcommunity.com">hello@driveforyourcommunity.com</a>.
              </p>
            </div>
            <div className="dfyc-col-cta">
              <a
                href="mailto:hello@driveforyourcommunity.com"
                className="dfyc-btn dfyc-btn-navy"
              >
                Contact PHQ
              </a>
            </div>
          </div>
        </section>
      </div>

      <div className="dfyc-footer-container">
        <PrivacyBanner
          containerStyle={{ padding: "10px 0 0", display: "flex", justifyContent: "center" }}
          privacyContainerStyle={{ padding: "10px 0", width: "60%", margin: "0 auto" }}
          showPrivacy={true}
          language={language}
          team={'ford'}
          imgStyle={{ paddingLeft: "30px" }}
          otherLinks={[
            { text: "CONTACT US", url: "/contact", target: '_blank' },
            { text: "PROGRAM TERMS AND CONDITIONS", url: "/terms-eligibility", target: '_blank' },
          ]}
        />
      </div>
    </>
  );
};

export default HomeScreen;
